<template>
	<modal v-if="popup_open && popup_name === 'create_event'" modal_name="create_event" type="medium" :title="`${$t('events.create_a')} ${$t('events.singular').toLowerCase()}`" icon="calendar-alt" class="tw-relative">
		<template v-slot:modal-content>
			<validation-observer ref="modal-create_event">
				<!-- TITLE -->
				<form-row>
					<form-field>
						<form-input v-model="new_event.title" :label="`${$t('events.name_of')} ${$t('events.singular').toLowerCase()}`" rules="required|max:145" name="name" :autofocus="true"></form-input>
					</form-field>
				</form-row>

				<!-- DATE -->
				<form-row>
					<form-field>
						<label class="form-label">{{ $t('events.date_of') }} {{ $t('events.singular').toLowerCase() }} </label>
						<date-range-picker :startDate="new_event.start_date" :endDate="new_event.end_date" @closed="updateDates" background="gray"></date-range-picker>
						<span class="form-validation-error" v-if="errors.date.length > 0">{{ errors.date[0] }}</span>
					</form-field>
				</form-row>

				<!-- START & END TIME -->
				<form-row class="tw-flex">
					<form-field-row>
						<custom-time-picker :label="$t('events.start_time')" class="tw-mr-4" :disabled="new_event.all_day || new_event.start_date === ''" @setTime="setStartTimeEvent"></custom-time-picker>
						<custom-time-picker :label="$t('events.end_time')" :disabled="new_event.all_day || new_event.start_date === ''" @setTime="setEndTimeEvent"></custom-time-picker>
					</form-field-row>
					<span class="form-validation-error" v-if="errors.time.length > 0">{{ errors.time[0] }}</span>
				</form-row>

				<!-- ALL DAY -->
				<form-row>
					<form-field>
						<form-switch :label="$t('events.all_day')" v-model="new_event.all_day" orientation="horizontal" class="tw-text-sm" @input="validateTime"></form-switch>
					</form-field>
				</form-row>

				<!-- CATEGORY -->
				<form-row>
					<form-field>
						<label class="form-label">{{ $t('events.category') }}</label>
						<form-select v-model="new_event.category_id" :options="categories" name_clean="id" display_name="name" name="category" :color_tag="true" :inside_modal="true" rules="required" :placeholder="`${$t('fields.select')} ${$t('events.category').toLowerCase()}`"></form-select>
					</form-field>
				</form-row>

				<!-- PRIVACY -->
				<form-row>
					<form-field>
						<label class="form-label">{{ $t('groups.privacy_and_accessibility') }}</label>
						<div class="tw-flex">
							<clickable-icon :class="{'active': new_event.status === 'public'}" bg_color="input" :rounded="false" class="new-group-privacy-option tw-mr-2" @click.native="setNewEventPrivacyOptionTo('public', true)">
								<i class="fal fa-eye"></i>
								<p>{{ $t('fields.public') }}</p>
							</clickable-icon>
							<clickable-icon :class="{'active': new_event.status === 'private' || new_event.status === 'secret'}" bg_color="input" :rounded="false" class="new-group-privacy-option" @click.native="setNewEventPrivacyOptionTo('private', true)">
								<i class="fal fa-lock"></i>
								<p>{{ $t('fields.private') }}</p>
							</clickable-icon>
						</div>

						<form-switch v-if="new_event.status === 'private' || new_event.status === 'secret'" v-model="new_event_is_hidden" :label="$t('groups.hide_on_overview')" orientation="horizontal" class="tw-mt-5 tw-ml-3 tw-text-sm" @toggle="toggleHidden"></form-switch>

						<p class="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-0 light-text tw-text-sm tw-leading-normal">
							<template v-if="new_event.status === 'public'">
								{{ $t('events.singular') }} {{ $t('groups_and_events.joined_by_anyone').toLowerCase() }}
							</template>
							<template v-else>
								{{ $t('groups_and_events.only_seen_by') }}
							</template>
							<span v-if="new_event.status === 'secret'" class="tw-block tw-mt-1">{{ $t('groups_and_events.only_user_access') }}.</span>
						</p>
					</form-field>
				</form-row>
			</validation-observer>
		</template>
		<template v-slot:modal-bottom>
			<div class="subtle-button slim" @click="closePopup">
				{{ $t('fields.cancel') }}
			</div>
			<div class="general-button btn-v2 slim" @click="dateTimeValidation">
				{{ $t('fields.create') }} {{ $t('events.singular').toLowerCase() }}
				<i class="far fa-check"></i>
			</div>
		</template>
	</modal>
</template>

<script>
import moment from 'moment';
import Global from '@/resources/assets/js/vue/mixins/Global';
import Modal from '@/resources/assets/js/vue/mixins/v2/Modal';
import HuddleEvent from '@/resources/assets/js/vue/lib/models/Event';
import Notification from '@/resources/assets/js/vue/lib/models/Notification';

import DateRangePicker from '@/resources/assets/js/vue/components/ui/DateRangePicker';
import FormSelect from '@/resources/assets/js/vue/components/ui/FormSelect.vue';
import TimePicker from '@/resources/assets/js/vue/components/TimePicker.vue';

export default {
	name: 'EventModal',

	mixins: [Global, Modal],

	components: {
		'form-select': FormSelect,
		'custom-time-picker': TimePicker,
		'date-range-picker': DateRangePicker,
	},

	props: {
		categories: {
			required: true,
		}
	},

	data() {
		return {
			new_event: new HuddleEvent(),
			new_event_is_hidden: false,
			errors: {
				date: [],
				time: [],
			},
			date_is_validated: false,
			time_is_validated: false,
		}
	},

	methods: {
		createEvent() {
			this.validateModal().then(() => {
				this.$store.commit('toggleSpinner', true);

				this.new_event.start_date = moment(this.new_event.start_date, 'MMMM DD, YYYY HH:mm').format('YYYY-MM-DD HH:mm');
				this.new_event.end_date = moment(this.new_event.end_date, 'MMMM DD, YYYY HH:mm').format('YYYY-MM-DD HH:mm');

				HuddleEvent.api().store(this.new_event).then((response) => {
					this.$store.commit('toggleSpinner', false);

					Notification.insert({
						data: [
							{
								title: `${this.$t('events.singular')} ${this.$t('groups.created')}.`,
							},
						],
					});

					this.new_event = new HuddleEvent();

					this.$router.push(
						{
							name: 'events.edit',
							params: {
								event_slug: response.slug,
							},
						},
					);
				}).catch(() => {
					this.$store.commit('toggleSpinner', false);

					Notification.insert({
						data: [
							{
								title: `${this.$t('events.singular')} ${this.$t('groups.could_not_be_created')}.`,
								status: 'error',
							},
						],
					});
				});
			});
		},

		setNewEventPrivacyOptionTo(privacy_option, btn_press = false) {
			if (btn_press && privacy_option === 'private') {
				if (this.new_event.status !== 'secret') {
					this.new_event.status = 'private';
				}
			} else {
				this.new_event.status = privacy_option;
			}
		},

		setTimeEvent(time, date_type) {
			let date = moment(date_type, 'MMMM DD, YYYY HH:mm').format('DD-MM-YYYY HH:mm');
			date = moment(date, 'DD-MM-YYYY HH:mm').toDate();
			date.setHours(time.substring(0, 2));
			date.setMinutes(time.slice(-2));

			return moment(date).format('MMMM DD, YYYY HH:mm');
		},

		setStartTimeEvent(time) {
			this.new_event.start_date = this.setTimeEvent(time, this.new_event.start_date);

			this.validateDate();
			this.validateTime();
		},

		setEndTimeEvent(time) {
			this.new_event.end_date = this.setTimeEvent(time, this.new_event.end_date);

			this.validateDate();
			this.validateTime();
		},

		updateDates(dates) {
			this.new_event.start_date = moment(dates.start_date, 'DD-MM-YYYY HH:mm').format('MMMM DD, YYYY HH:mm');

			if (dates.end_date !== 'Invalid date') {
				this.new_event.end_date = moment(dates.end_date, 'DD-MM-YYYY HH:mm').format('MMMM DD, YYYY HH:mm');
			} else {
				this.new_event.end_date = this.new_event.start_date;
			}

			this.validateDate();
			this.validateTime();
		},

		validateDate() {
			const start_date = moment(this.new_event.start_date, 'MMMM DD, YYYY HH:mm').format('YYYY-MM-DD');
			const today = moment(new Date()).format('YYYY-MM-DD');
			this.errors.date = [];
			this.date_is_validated = false;

			if (this.new_event.start_date === '') {
				this.date_is_validated = false;
				this.errors.date.push(this.$t('events.date_not_selected'));
			} else if (this.new_event.start_date !== '' && today > start_date) {
				this.date_is_validated = false;
				this.errors.date.push(this.$t('events.incorrect_start_date'));
			} else {
				this.errors.date = [];
				this.date_is_validated = true;
			}
		},

		validateTime() {
			const start_date = moment(this.new_event.start_date, 'MMMM DD, YYYY HH:mm').format('YYYY-MM-DD');
			const end_date = moment(this.new_event.end_date, 'MMMM DD, YYYY HH:mm').format('YYYY-MM-DD');
			const today = moment(new Date()).format('YYYY-MM-DD');
			const time_now = moment(new Date()).format('HH:mm');
			const start_time = moment(this.new_event.start_date, 'MMMM DD, YYYY HH:mm').format('HH:mm');
			const end_time = moment(this.new_event.end_date, 'MMMM DD, YYYY HH:mm').format('HH:mm');
			this.errors.time = [];
			this.time_is_validated = false;

			if (this.new_event.start_date !== '' && !this.new_event.all_day && (today === start_date && today <= end_date && time_now > start_time)) {
				this.errors.time.push(this.$t('events.incorrect_start_time'));
			} else if (this.new_event.start_date !== '' && !this.new_event.all_day && ((today === start_date && today === end_date && start_time >= end_time) || (start_date === end_date && start_time > end_time))) {
				this.errors.time.push(this.$t('events.incorrect_end_time'));
			} else {
				this.errors.time = [];
				this.time_is_validated = true;
			}
		},

		dateTimeValidation() {
			this.validateDate();
			this.validateTime();

			if (this.date_is_validated && this.time_is_validated) {
				this.createEvent();
				this.date_is_validated = false;
				this.time_is_validated = false;
			}
		},

		setNewGroupPrivacyOptionTo(privacy_option, btn_press = false) {
			if (btn_press && privacy_option === 'private') {
				if (this.new_event.status !== 'secret') {
					this.new_event.status = 'private';
				}
			} else {
				this.new_event.status = privacy_option;
			}
		},

		toggleHidden(bool) {
			if (bool === true) {
				this.setNewGroupPrivacyOptionTo('secret');
			} else {
				this.setNewGroupPrivacyOptionTo('private');
			}
		},
	},
}
</script>